import { create } from "zustand";
import { ApiError, api } from "./expertApi";
import type {
    AskContext,
    ConverseUi,
    DecisionDraft,
    HitlQueueResponse,
    ItemDetail,
    Persona,
} from "./aidwops.types";
import {
    getWhitelistRequestWebsite,
    isSslBypassRequest,
    isWhitelistRequest,
} from "./aidwops.utils";

export interface AidwopsMessage {
    id: string;
    role: "user" | "assistant";
    content: string;
    response_id?: string | null;
    raw_output?: unknown[];
    handledBy?: "orchestrator" | "queue";
    ui?: ConverseUi | null;
    /**
     * Set only on the queue message a queue view opens with, and says which persona's queue it shows.
     * That message follows the live queue (see `setQueue`); queue answers to chat commands, such as
     * "show only low-risk", are snapshots and are left as they were.
     */
    queueScope?: Persona;
}

interface AidwopsState {
    initialPrompts: string[];
    queue: HitlQueueResponse | null;
    /** Which persona the loaded `queue` belongs to, so a queue view never opens with another persona's items. */
    queueScope: Persona | null;
    isQueueLoading: boolean;
    queueError: string | null;
    messages: AidwopsMessage[];
    draft: string;
    isSubmitting: boolean;
    editingMessageId: string | null;
    editingDraft: string;
    /** ID of the queue item opened for review, or null when the review panel is closed. */
    selectedItem: string | null;
    /** Full detail payload for the currently selected queue item, once fetched. */
    selectedItemDetail: ItemDetail | null;
    isSelectedItemLoading: boolean;
    selectedItemError: string | null;
    /** A decision a queue command prepared in chat ("decline #7 because ..."); the review panel picks it up and prefills itself. */
    pendingDraft: DecisionDraft | null;
    /** Whether the custom filter view is visible in the queue. */
    viewCustomFilter: boolean;
    fetchInitialPrompts: (
        persona: Persona,
        accessToken: string
    ) => Promise<void>;
    fetchQueue: (persona: Persona, accessToken: string) => Promise<void>;
    clearInitialPrompts: () => void;
    clearQueue: () => void;
    setQueueError: (message: string | null) => void;
    appendMessage: (message: AidwopsMessage) => void;
    setDraft: (draft: string) => void;
    setIsSubmitting: (isSubmitting: boolean) => void;
    setEditingMessageId: (messageId: string | null) => void;
    setEditingDraft: (draft: string) => void;
    setSelectedItem: (itemId: string | null) => void;
    /** Fetches full item detail for the selected queue item id. */
    fetchSelectedItemDetail: (
        itemId: string,
        accessToken: string
    ) => Promise<void>;
    setSelectedItemDetail: (item: ItemDetail) => void;
    clearPendingDraft: () => void;
    setViewCustomFilter: (viewCustomFilter: boolean) => void;
    /** Stores a freshly read queue and brings the opening queue message of that persona up to date. */
    setQueue: (queue: HitlQueueResponse | null, scope?: Persona) => void;
    appendMessageIfEmpty: (message: AidwopsMessage) => boolean;
    /** Clears the chat history (and the previous response id carried with it) to start a fresh conversation. */
    resetChat: () => void;
    /** Submits a prompt to the assistant API, appending the user/assistant messages to the shared chat history. */
    submitPrompt: (
        prompt: string,
        getAccessToken: () => Promise<string>,
        context?: AskContext
    ) => Promise<void>;
}

let latestPromptsRequestId = 0;
let latestQueueRequestId = 0;
let latestItemDetailRequestId = 0;

/** The queue item a queue command's answer is about, if the screen should open it. */
function targetItemOf(
    ui: ConverseUi | null | undefined
): { id: string; draft: DecisionDraft | null } | null {
    switch (ui?.type) {
        case "detail":
        case "why":
        case "created":
            return { id: ui.item.id, draft: null };
        case "decision_draft":
            return { id: ui.draft.id, draft: ui.draft };
        case "ticket_preview":
            return { id: ui.id, draft: null };
        default:
            return null;
    }
}

const useAidwopsStore = create<AidwopsState>((set, get) => ({
    initialPrompts: [],
    queue: null,
    queueScope: null,
    isQueueLoading: false,
    queueError: null,
    messages: [],
    draft: "",
    isSubmitting: false,
    editingMessageId: null,
    editingDraft: "",
    selectedItem: null,
    selectedItemDetail: null,
    isSelectedItemLoading: false,
    selectedItemError: null,
    pendingDraft: null,
    viewCustomFilter: false,
    fetchInitialPrompts: async (persona, accessToken) => {
        const requestId = ++latestPromptsRequestId;
        set({ initialPrompts: [] });

        try {
            const initialPrompts = await api.getPrompts(persona, accessToken);
            if (requestId === latestPromptsRequestId) {
                set({ initialPrompts });
            }
        } catch {
            if (requestId === latestPromptsRequestId) {
                set({ initialPrompts: [] });
            }
        }
    },
    fetchQueue: async (persona, accessToken) => {
        const requestId = ++latestQueueRequestId;
        set({
            queue: null,
            queueScope: persona,
            isQueueLoading: true,
            queueError: null,
        });

        try {
            const queue = await api.getQueue(persona, accessToken);
            if (requestId === latestQueueRequestId) {
                set({ queue, isQueueLoading: false });
            }
        } catch (error) {
            if (requestId === latestQueueRequestId) {
                set({
                    queue: null,
                    isQueueLoading: false,
                    queueError:
                        error instanceof Error ? error.message : String(error),
                });
            }
        }
    },
    clearInitialPrompts: () => {
        latestPromptsRequestId += 1;
        set({ initialPrompts: [] });
    },
    clearQueue: () => {
        latestQueueRequestId += 1;
        set({
            queue: null,
            queueScope: null,
            isQueueLoading: false,
            queueError: null,
        });
    },
    setQueueError: (queueError) => set({ queueError, isQueueLoading: false }),
    appendMessage: (message) =>
        set((state) => ({ messages: [...state.messages, message] })),
    appendMessageIfEmpty: (message) => {
        let appended = false;
        set((state) => {
            if (state.messages.length > 0) {
                return state;
            }

            appended = true;
            return { messages: [message] };
        });
        return appended;
    },
    setDraft: (draft) => set({ draft }),
    setIsSubmitting: (isSubmitting) => set({ isSubmitting }),
    setEditingMessageId: (editingMessageId) => set({ editingMessageId }),
    setEditingDraft: (editingDraft) => set({ editingDraft }),
    setSelectedItem: (selectedItem) => {
        latestItemDetailRequestId += 1;
        set({
            selectedItem,
            selectedItemDetail: null,
            selectedItemError: null,
            isSelectedItemLoading: false,
        });
    },
    fetchSelectedItemDetail: async (itemId, accessToken) => {
        const requestId = ++latestItemDetailRequestId;
        set({ isSelectedItemLoading: true, selectedItemError: null });

        try {
            const detail = await api.getQueueItem(itemId, accessToken);
            if (requestId === latestItemDetailRequestId) {
                set({ selectedItemDetail: detail, isSelectedItemLoading: false });
            }
        } catch (error) {
            if (requestId === latestItemDetailRequestId) {
                set({
                    selectedItemDetail: null,
                    isSelectedItemLoading: false,
                    selectedItemError:
                        error instanceof Error ? error.message : String(error),
                });
            }
        }
    },
    setSelectedItemDetail: (selectedItemDetail) => set({ selectedItemDetail }),
    clearPendingDraft: () => set({ pendingDraft: null }),
    setViewCustomFilter: (viewCustomFilter) => set({ viewCustomFilter }),
    setQueue: (queue, scope) =>
        set((state) => ({
            queue,
            queueScope: queue && scope ? scope : state.queueScope,
            messages:
                queue && scope
                    ? state.messages.map((message) =>
                          message.queueScope === scope &&
                          message.handledBy === "queue" &&
                          message.ui?.type === "queue"
                              ? {
                                    ...message,
                                    content: queue.summary.headline,
                                    ui: {
                                        type: "queue",
                                        summary: queue.summary,
                                        items: queue.items,
                                    },
                                }
                              : message
                      )
                    : state.messages,
        })),
    resetChat: () =>
        set({
            messages: [],
            draft: "",
            isSubmitting: false,
            editingMessageId: null,
            editingDraft: "",
            selectedItem: null,
            selectedItemDetail: null,
            isSelectedItemLoading: false,
            selectedItemError: null,
            pendingDraft: null,
        }),
    submitPrompt: async (prompt, getAccessToken, context) => {
        const trimmedPrompt = prompt.trim();

        if (!trimmedPrompt || get().isSubmitting) {
            return;
        }

        const previousResponseId = [...get().messages]
            .reverse()
            .find((message) => message.response_id)?.response_id ?? undefined;

        set((state) => ({
            messages: [
                ...state.messages,
                {
                    id: crypto.randomUUID(),
                    role: "user",
                    content: trimmedPrompt,
                },
            ],
            draft: "",
            isSubmitting: true,
        }));

        try {
            if (isWhitelistRequest(trimmedPrompt)) {
                const website = getWhitelistRequestWebsite(trimmedPrompt);
                if (!website) {
                    throw new Error(
                        "Unable to identify the website for the whitelist request."
                    );
                }

                await new Promise((resolve) => setTimeout(resolve, 300));

                set((state) => ({
                    messages: [
                        ...state.messages,
                        {
                            id: crypto.randomUUID(),
                            role: "assistant",
                            content:
                                "Sure, fill in a few details and I'll raise a whitelist request to the network security team.",
                            ui: { type: "whitelist_request", url: website },
                        },
                    ],
                }));
                return;
            }

            if (isSslBypassRequest(trimmedPrompt)) {
                await new Promise((resolve) => setTimeout(resolve, 300));

                set((state) => ({
                    messages: [
                        ...state.messages,
                        {
                            id: crypto.randomUUID(),
                            role: "assistant",
                            content:
                                "Sure, fill in a few details and I'll raise an SSL bypass request to the network security team.",
                            ui: { type: "ssl_bypass_request" },
                        },
                    ],
                }));
                return;
            }

            const accessToken = await getAccessToken();
            let response;
            try {
                response = await api.ask(
                    trimmedPrompt,
                    accessToken,
                    previousResponseId,
                    context
                );
            } catch (error) {
                // The queue context makes the backend demand the engineer role for every message. Someone without it still
                // gets the assistant: ask again without the context, which only turns off the queue commands.
                if (context && error instanceof ApiError && error.status === 403) {
                    response = await api.ask(
                        trimmedPrompt,
                        accessToken,
                        previousResponseId
                    );
                } else {
                    throw error;
                }
            }

            set((state) => ({
                messages: [
                    ...state.messages,
                    {
                        id: crypto.randomUUID(),
                        role: "assistant",
                        content: response.answer ?? "No answer returned.",
                        response_id: response.response_id,
                        raw_output: response.raw_output,
                        handledBy: response.handledBy,
                        ui: response.ui,
                    },
                ],
            }));

            // A queue command that is about one item ("tell me about #1", "decline #7 because ...") opens it in the review panel.
            const target =
                response.handledBy === "queue"
                    ? targetItemOf(response.ui)
                    : null;
            if (target) {
                // Re-selecting the item that is already open would blank its panel (the detail is only re-fetched when the selection changes).
                if (get().selectedItem !== target.id) {
                    get().setSelectedItem(target.id);
                }
                set({ pendingDraft: target.draft });
            }
        } catch (error) {
            set((state) => ({
                messages: [
                    ...state.messages,
                    {
                        id: crypto.randomUUID(),
                        role: "assistant",
                        content: `Error: ${error}`,
                    },
                ],
            }));
        } finally {
            set({ isSubmitting: false });
        }
    },
}));

export default useAidwopsStore;
